import { World } from "./world";
import { KIND_WRECKAGE, OWNER_NEUTRAL, OWNER_PLAYER, OWNER_ENEMY, WRECKAGE_METAL_FRACTION } from "./constants";
import { getFaction, type Faction } from "../data";

// Helpers for creating entities that aren't produced by construction: map
// features, wreckage left by the dead, and the initial match state.

export function spawnFeature(w: World, type: "tree" | "rock", x: number, y: number): number {
  const e = w.spawn(type, OWNER_NEUTRAL, x, y);
  if (e < 0) return -1;
  const def = w.def(e);
  w.reclaimMetalStore[e] = def.reclaimMetal ?? 0;
  w.reclaimEnergyStore[e] = def.reclaimEnergy ?? 0;
  return e;
}

/** Convert a dying unit/building into reclaimable wreckage. */
export function spawnWreckage(w: World, from: number): number {
  const def = w.def(from);
  const e = w.spawn(def.id, OWNER_NEUTRAL, w.posX[from], w.posY[from], { heading: w.heading[from] });
  if (e < 0) return -1;
  w.kind[e] = KIND_WRECKAGE;
  const built = w.buildProgress[from]; // fraction actually built
  w.maxHealth[e] = Math.max(20, def.maxHealth * 0.5);
  w.health[e] = w.maxHealth[e];
  w.reclaimMetalStore[e] = def.metalCost * WRECKAGE_METAL_FRACTION * built;
  w.buildProgress[e] = 1;
  return e;
}

export interface MatchConfig {
  playerFaction: Faction;
  enemyFaction: Faction;
}

/** Place commanders + map features and seed starting resources. */
export function initMatch(w: World, cfg: MatchConfig): { playerCommander: number; enemyCommander: number } {
  // Map features.
  for (const f of w.gmap.def.features) {
    spawnFeature(w, f.type, f.x, f.y);
  }

  const pf = getFaction(cfg.playerFaction);
  const ef = getFaction(cfg.enemyFaction);
  let playerCommander = -1;
  let enemyCommander = -1;
  for (const s of w.gmap.def.starts) {
    if (s.owner === OWNER_PLAYER) {
      playerCommander = w.spawn(pf.commanderId, OWNER_PLAYER, s.x, s.y, { heading: -Math.PI / 4 });
    } else if (s.owner === OWNER_ENEMY) {
      enemyCommander = w.spawn(ef.commanderId, OWNER_ENEMY, s.x, s.y, { heading: (3 * Math.PI) / 4 });
    }
  }

  // Starting resources (streaming buffer). Caps are recomputed by economy from
  // the commander's storage each tick; seed storage below cap.
  for (const owner of [OWNER_PLAYER, OWNER_ENEMY]) {
    const eco = w.eco[owner];
    eco.metal = 700;
    eco.energy = 700;
    eco.metalCap = 750;
    eco.energyCap = 750;
  }

  return { playerCommander, enemyCommander };
}
